import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAgentRoles } from "../agentRoles/useAgentRoles";
import type { ToolBlacklistItem } from "./api";
import { isRoleCriticalTool } from "./footgun";
import { useAddToolBlacklistItem } from "./useAddToolBlacklistItem";
import { useRemoveToolBlacklistItem } from "./useRemoveToolBlacklistItem";
import { useToolBlacklist } from "./useToolBlacklist";

// Секция «Запрещённые инструменты роли» на `/settings/tool-blacklist`.
// Человек выбирает роль (coder/reviewer/...), видит список запрещённых
// инструментов per-(org, role), добавляет и удаляет их (source=manual).
// Зеркало `ReviewerChecklistSection`: Select роли вместо ввода репо +
// footgun-предупреждение про роль-критичные инструменты (спека § «Footgun»).
//
// Управляющая часть (форма add + список + модалка delete) вынесена в
// `ToolBlacklistManager`, который принимает выбранную роль пропсом. Это
// отделяет хуки мутаций (зависят от роли) от Mantine Select, чей onChange в
// jsdom не зовётся — менеджер тестируется напрямую с заданной ролью.

// Цвет Badge источника: правка человека (manual) vs самообучение.
function sourceColor(source: ToolBlacklistItem["source"]): string {
  return source === "manual" ? "blue" : "grape";
}

// Что показать под Select'ом: управление запретами выбранной роли или
// подсказку «выберите роль». Вынесено чистой функцией — Mantine Select в
// jsdom нельзя выбрать (floating-ui требует browser-API), поэтому обе ветки
// тестируем здесь напрямую, а не через клик по выпадашке.
export function renderManagerOrHint(
  roleName: string | null,
  t: ReturnType<typeof useTranslation>["t"],
): ReactElement {
  if (roleName === null) {
    return <Text c="dimmed">{t("toolBlacklist.selectRole")}</Text>;
  }
  return <ToolBlacklistManager roleName={roleName} />;
}

export function ToolBlacklistSection(): ReactElement {
  const { t } = useTranslation();
  const roles = useAgentRoles();
  const [roleName, setRoleName] = useState<string | null>(null);

  const roleOptions = (roles.data ?? []).map((role) => ({
    value: role.role_name,
    label: t(`agentRoles.role.${role.role_name}`, {
      defaultValue: role.role_name,
    }),
  }));

  return (
    <Stack gap="md">
      <Title order={2}>{t("toolBlacklist.title")}</Title>
      <Text c="dimmed">{t("toolBlacklist.intro")}</Text>

      <Select
        label={t("toolBlacklist.roleLabel")}
        description={t("toolBlacklist.roleHint")}
        placeholder={t("toolBlacklist.rolePlaceholder")}
        data={roleOptions}
        value={roleName}
        onChange={setRoleName}
        disabled={roles.isLoading}
        error={roles.isError ? t("toolBlacklist.rolesError") : undefined}
      />

      {renderManagerOrHint(roleName, t)}
    </Stack>
  );
}

// Управление запретами выбранной роли: форма add (+ footgun-Alert), список и
// модалка подтверждения delete. Роль — проп (Select обойдён); хуки мутаций
// привязаны к ней.
export function ToolBlacklistManager({
  roleName,
}: {
  readonly roleName: string;
}): ReactElement {
  const { t } = useTranslation();
  const [newTool, setNewTool] = useState("");
  const [deleting, setDeleting] = useState<ToolBlacklistItem | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const blacklist = useToolBlacklist(roleName);
  const addMutation = useAddToolBlacklistItem();
  const removeMutation = useRemoveToolBlacklistItem();

  const trimmedTool = newTool.trim();
  const canAdd = trimmedTool.length > 0;
  const footgun = canAdd && isRoleCriticalTool(roleName, trimmedTool);

  const cancelDelete = (): void => {
    setDeleting(null);
    setRemoveError(null);
  };

  const handleAdd = (): void => {
    setAddError(null);
    addMutation.mutate(
      { role_name: roleName, tool_name: trimmedTool },
      {
        onSuccess: () => {
          setNewTool("");
        },
        onError: (error) => {
          setAddError(t("toolBlacklist.addError", { message: error.message }));
        },
      },
    );
  };

  return (
    <Stack gap="md">
      {addError !== null ? (
        <Alert color="red" title={t("toolBlacklist.errorHeading")}>
          {addError}
        </Alert>
      ) : null}

      <BlacklistBody
        blacklist={blacklist}
        newTool={newTool}
        setNewTool={setNewTool}
        canAdd={canAdd}
        footgun={footgun}
        adding={addMutation.isPending}
        onAdd={handleAdd}
        onDelete={(item) => {
          setRemoveError(null);
          setDeleting(item);
        }}
        t={t}
      />

      <Modal
        opened={deleting !== null}
        onClose={cancelDelete}
        title={t("toolBlacklist.delete.confirmTitle")}
        centered
      >
        {deleting !== null ? (
          <Stack gap="md">
            <Text>
              {t("toolBlacklist.delete.confirmText", {
                tool: deleting.tool_name,
              })}
            </Text>
            {removeError !== null ? (
              <Alert color="red" title={t("toolBlacklist.errorHeading")}>
                {removeError}
              </Alert>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={cancelDelete}>
                {t("toolBlacklist.modalCancel")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  removeMutation.mutate(
                    {
                      roleName: deleting.role_name,
                      toolName: deleting.tool_name,
                    },
                    {
                      onSuccess: () => {
                        setDeleting(null);
                      },
                      onError: (error) => {
                        setRemoveError(
                          t("toolBlacklist.removeError", {
                            message: error.message,
                          }),
                        );
                      },
                    },
                  );
                }}
                loading={removeMutation.isPending}
              >
                {t("toolBlacklist.delete.confirmButton")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

// Тело секции: форма добавления (+ footgun-Alert) и список запретов.
// Loader/Error/empty/таблица — взаимоисключающие состояния списка.
export function BlacklistBody({
  blacklist,
  newTool,
  setNewTool,
  canAdd,
  footgun,
  adding,
  onAdd,
  onDelete,
  t,
}: {
  readonly blacklist: ReturnType<typeof useToolBlacklist>;
  readonly newTool: string;
  readonly setNewTool: (value: string) => void;
  readonly canAdd: boolean;
  readonly footgun: boolean;
  readonly adding: boolean;
  readonly onAdd: () => void;
  readonly onDelete: (item: ToolBlacklistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm">
        <TextInput
          style={{ flex: 1 }}
          label={t("toolBlacklist.newToolLabel")}
          placeholder={t("toolBlacklist.newToolPlaceholder")}
          value={newTool}
          onChange={(event) => {
            setNewTool(event.currentTarget.value);
          }}
        />
        <Button onClick={onAdd} disabled={!canAdd} loading={adding}>
          {t("toolBlacklist.addButton")}
        </Button>
      </Group>

      {footgun ? (
        <Alert color="orange" title={t("toolBlacklist.footgun.title")}>
          {t("toolBlacklist.footgun.text")}
        </Alert>
      ) : null}

      <BlacklistList blacklist={blacklist} onDelete={onDelete} t={t} />
    </Stack>
  );
}

// Список запретов: один из четырёх взаимоисключающих результатов запроса.
function BlacklistList({
  blacklist,
  onDelete,
  t,
}: {
  readonly blacklist: ReturnType<typeof useToolBlacklist>;
  readonly onDelete: (item: ToolBlacklistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  if (blacklist.isPending) {
    return <Loader />;
  }
  if (blacklist.isError) {
    return (
      <Alert color="red" title={t("toolBlacklist.errorHeading")}>
        {t("toolBlacklist.loadingError")}
      </Alert>
    );
  }
  if (blacklist.data.length === 0) {
    return <Text c="dimmed">{t("toolBlacklist.empty")}</Text>;
  }
  return (
    <Table withTableBorder withColumnBorders striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("toolBlacklist.colTool")}</Table.Th>
          <Table.Th>{t("toolBlacklist.colSource")}</Table.Th>
          <Table.Th>{t("toolBlacklist.colManage")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {blacklist.data.map((item) => (
          <Table.Tr key={item.tool_name}>
            <Table.Td>
              <Group gap="xs">
                <Text>{item.tool_name}</Text>
                {item.known ? null : (
                  <Badge color="orange" variant="light">
                    {t("toolBlacklist.unknownTool")}
                  </Badge>
                )}
              </Group>
            </Table.Td>
            <Table.Td>
              <Badge variant="light" color={sourceColor(item.source)}>
                {t(`toolBlacklist.source.${item.source}`)}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Button
                size="xs"
                color="red"
                variant="subtle"
                onClick={() => {
                  onDelete(item);
                }}
              >
                {t("toolBlacklist.deleteButton")}
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
